import React from 'react';
import DatePicker from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import TimePicker from 'react-multi-date-picker/plugins/time_picker';
import {
  formatGregorianDateForInput,
  formatGregorianDateTimeForInput,
  parseGregorianDate,
  parseGregorianDateTime,
  toGregorian,
} from '../utils/dateUtils';

function baseButtonClasses(kind = 'secondary') {
  if (kind === 'danger') {
    return 'rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600 transition hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed';
  }
  return 'rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 transition hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed';
}


function datePickerValueToGregorianDateInput(val) {
  if (!val) return '';
  // react-multi-date-picker DateObject keeps the selected Persian calendar
  // day in val.year / val.month.number / val.day. Using toDate() for date-only
  // values may shift one day in some browser/timezone combinations. Convert the
  // calendar day explicitly so a selected/visible Persian day is stored exactly.
  try {
    const year = Number(val?.year);
    const month = Number(val?.month?.number ?? val?.month);
    const day = Number(val?.day);
    if (year && month && day) {
      return formatGregorianDateForInput(toGregorian(year, month, day));
    }
  } catch (_) {
    // fall back below
  }
  const jsDate = typeof val?.toDate === 'function' ? val.toDate() : null;
  return formatGregorianDateForInput(jsDate);
}

function wrapperClassName(extra = '') {
  return ['flex items-stretch gap-2 w-full', extra].filter(Boolean).join(' ');
}

export function JalaliDateInput({
  value,
  onChange,
  placeholder = 'انتخاب تاریخ',
  disabled = false,
  clearable = true,
  inputClass = 'input',
  containerClassName = '',
  calendarPosition = 'bottom-right',
  minDate,
  maxDate,
}) {
  const pickerValue = parseGregorianDate(value);

  return (
    <div className={wrapperClassName(containerClassName)}>
      <DatePicker
        value={pickerValue}
        onChange={(val) => {
          if (!val) {
            onChange?.('');
            return;
          }
          onChange?.(datePickerValueToGregorianDateInput(val));
        }}
        calendar={persian}
        locale={persian_fa}
        calendarPosition={calendarPosition}
        inputClass={inputClass}
        containerClassName="flex-1"
        placeholder={placeholder}
        disabled={disabled}
        format="YYYY/MM/DD"
        minDate={minDate ? parseGregorianDate(minDate) : undefined}
        maxDate={maxDate ? parseGregorianDate(maxDate) : undefined}
      />
      {clearable && (
        <button
          type="button"
          className={baseButtonClasses('danger')}
          onClick={() => onChange?.('')}
          disabled={disabled || !value}
          title="پاک کردن تاریخ"
        >
          پاک
        </button>
      )}
    </div>
  );
}

export function JalaliDateTimeInput({
  value,
  onChange,
  placeholder = 'انتخاب تاریخ و ساعت',
  disabled = false,
  clearable = true,
  inputClass = 'input',
  containerClassName = '',
  calendarPosition = 'bottom-right',
}) {
  const pickerValue = parseGregorianDateTime(value);

  return (
    <div className={wrapperClassName(containerClassName)}>
      <DatePicker
        value={pickerValue}
        onChange={(val) => {
          if (!val) {
            onChange?.('');
            return;
          }
          const jsDate = typeof val?.toDate === 'function' ? val.toDate() : null;
          onChange?.(formatGregorianDateTimeForInput(jsDate));
        }}
        calendar={persian}
        locale={persian_fa}
        calendarPosition={calendarPosition}
        inputClass={inputClass}
        containerClassName="flex-1"
        placeholder={placeholder}
        disabled={disabled}
        format="YYYY/MM/DD HH:mm"
        plugins={[<TimePicker key="time-picker" position="bottom" hideSeconds />]}
      />
      {clearable && (
        <button
          type="button"
          className={baseButtonClasses('danger')}
          onClick={() => onChange?.('')}
          disabled={disabled || !value}
          title="پاک کردن تاریخ و ساعت"
        >
          پاک
        </button>
      )}
    </div>
  );
}

export default JalaliDateInput;
